/**
 * SIRH LOT 4.6 — MODÈLE D'AFFICHAGE DU PLANNING (helpers PURS, testables).
 *
 *   · palette POSTES bornée (Live Control) et couleur d'un shift : le POSTE
 *     est la couleur métier principale ; sans poste → neutre « Poste non
 *     renseigné », jamais une couleur inventée ;
 *   · axe de la vue Jour : amplitude configurée par l'établissement
 *     (display_day_start/end), ÉLARGIE automatiquement si un shift déborde —
 *     un shift à 13:00 n'est jamais caché silencieusement (§29-30). Toutes
 *     les heures sont RELATIVES AU MINUIT CIVIL du jour de référence : aucune
 *     frontière de jour codée en dur (la politique accepte 0..23 des deux
 *     côtés), une fin ≤ début signifie simplement « le lendemain ».
 */

export const POSITION_PALETTE = ['violet', 'blue', 'cyan', 'emerald', 'amber', 'rose', 'coral', 'lime', 'indigo', 'slate'] as const
export type PositionTone = typeof POSITION_PALETTE[number]

export const TONE_LABELS: Record<PositionTone, string> = {
  violet: 'Violet', blue: 'Bleu', cyan: 'Cyan', emerald: 'Émeraude', amber: 'Ambre', rose: 'Rose',
  coral: 'Corail', lime: 'Lime', indigo: 'Indigo', slate: 'Ardoise',
}

/** Couleur d'un shift : celle du poste ; sans poste → 'none' (neutre, jamais métier). */
export function shiftTone(s: { position_id: string | null; position_color: string | null }): PositionTone | 'none' {
  if (!s.position_id) return 'none'
  if (s.position_color && (POSITION_PALETTE as readonly string[]).includes(s.position_color)) return s.position_color as PositionTone
  // Poste sans couleur configurée : dérivée STABLE de l'identifiant (jamais aléatoire),
  // parmi les tons colorés (slate exclu pour rester distinguable du « sans poste »).
  const colored = POSITION_PALETTE.filter((t) => t !== 'slate')
  let h = 0
  for (const c of s.position_id) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return colored[h % colored.length]!
}

/* ─── Axe de la vue Jour ─── */

export interface DayAxis {
  /** Heures locales affichées, dans l'ordre (ex. 18,19,…,23,0,1,…,7). */
  hours: number[]
  /** Heure de début de l'axe, comptée depuis le minuit civil du jour de référence (0..23). */
  base: number
  /** Nombre de créneaux horaires. */
  span: number
  /** L'amplitude configurée a été élargie pour ne pas cacher un shift. */
  widened: boolean
}

/** Fin exclusive d'un intervalle horaire : une fin ≤ début est le lendemain (un shift dure moins de 24 h). */
function endAfter(start: number, end: number): number { return end <= start ? end + 24 : end }

/**
 * Heure de bascule de la JOURNÉE D'AFFICHAGE de la vue Jour : quand l'axe
 * traverse minuit (club 18 → 8), un shift qui commence avant `display_day_end`
 * appartient à la nuit de la veille — la vue « Samedi 5 » montre la nuit du
 * samedi au dimanche, et un shift du dimanche 00:15 s'y place à 24 h 15. Quand
 * l'axe ne traverse pas minuit, la journée est civile (bascule à 0 h).
 */
export function displayRollover(policy: { display_day_start: number; display_day_end: number }): number {
  return policy.display_day_end <= policy.display_day_start ? policy.display_day_end : 0
}

/**
 * Calcule l'axe : amplitude configurée [start → end[ (end ≤ start = lendemain),
 * élargie aux shifts qui dépassent (bornes arrondies à l'heure). Les shifts
 * fournis COMMENCENT le jour de référence : leur début est leur heure civile,
 * jamais recalée sur une frontière de jour arbitraire.
 */
export function dayAxis(
  policy: { display_day_start: number; display_day_end: number },
  shifts: Array<{ startHour: number; endHour: number }>,
): DayAxis {
  let start = policy.display_day_start
  let endExcl = endAfter(start, policy.display_day_end)
  const cfgStart = start, cfgEnd = endExcl
  for (const s of shifts) {
    const a = Math.floor(s.startHour)
    const b = endAfter(a, Math.ceil(s.endHour))
    if (a < start) start = a
    if (b > endExcl) endExcl = b
  }
  // Plafond : 36 h d'axe (au-delà, l'axe redevient illisible ; on garde la borne).
  if (endExcl - start > 36) endExcl = start + 36
  const hours: number[] = []
  for (let h = start; h < endExcl; h++) hours.push(h % 24)
  return { hours, base: start, span: hours.length, widened: start < cfgStart || endExcl > cfgEnd }
}

/** Position/largeur (en %) d'une barre sur l'axe — mêmes heures relatives que l'axe. */
export function barGeometry(axis: DayAxis, startHour: number, endHour: number): { left: number; width: number } {
  const A = startHour; const B = endAfter(A, endHour)
  const left = Math.max(0, ((A - axis.base) / axis.span) * 100)
  const right = Math.min(B, axis.base + axis.span)
  const width = Math.max(2, ((right - Math.max(A, axis.base)) / axis.span) * 100)
  return { left, width }
}

/* ─── Densités ─── */
export const DENSITIES = ['comfortable', 'compact', 'ultra'] as const
export type Density = typeof DENSITIES[number]
export const DENSITY_LABELS: Record<Density, string> = { comfortable: 'Confortable', compact: 'Compacte', ultra: 'Ultra compacte' }

/** Durée « 7 h », « 6 h 40 », « −2 h 15 ». */
export function fmtDuration(min: number): string {
  const sign = min < 0 ? '−' : ''
  const abs = Math.abs(Math.round(min))
  const hh = Math.floor(abs / 60); const mm = abs % 60
  return mm ? `${sign}${hh} h ${String(mm).padStart(2, '0')}` : `${sign}${hh} h`
}

/** Initiales pour avatar : « Hugo Étienne » → « HÉ ». */
export function initials(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('')
}

/* ─── Absences (Lot 4.7) ───
   Une absence n'est PAS un shift : même palette de tokens (couleur du TYPE,
   configurée côté serveur), rendu hachuré/pointillé distinct, jamais draggable.
   Sans couleur configurée → ardoise (neutre), jamais une teinte inventée. */

export function leaveTone(color: string | null | undefined): PositionTone {
  return color && (POSITION_PALETTE as readonly string[]).includes(color) ? color as PositionTone : 'slate'
}

export interface LeaveSpan { starts_on: string; ends_on: string; start_half: boolean; end_half: boolean; hours_from: number | null; hours_to: number | null }

/** « 23:00 » depuis des minutes locales ; > 1440 = lendemain, ramené sur 24 h. */
export function minutesToHm(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/** « HH:MM » → minutes, null si la saisie n'est pas une heure. */
export function hmToMinutes(hm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim()); if (!m) return null
  const h = Number(m[1]); const mi = Number(m[2])
  return h >= 0 && h <= 23 && mi >= 0 && mi <= 59 ? h * 60 + mi : null
}

/**
 * Portion d'absence visible UN jour donné : « 23:00–06:00 » (heures), « Matin »
 * (dernier jour en demi-journée, ou journée seule avec end_half), « Après-midi »
 * (premier jour en demi-journée), sinon « Toute la journée ». Sémantique du
 * domaine Congés (leaveDaysCount) : start_half = commence l'après-midi,
 * end_half = finit à midi.
 */
export function leaveDayLabel(l: LeaveSpan, day: string): string {
  if (l.hours_from !== null && l.hours_to !== null && l.starts_on === l.ends_on) return `${minutesToHm(l.hours_from)}–${minutesToHm(l.hours_to)}`
  if (l.starts_on === l.ends_on) return l.start_half ? 'Après-midi' : l.end_half ? 'Matin' : 'Toute la journée'
  if (day === l.starts_on && l.start_half) return 'Après-midi'
  if (day === l.ends_on && l.end_half) return 'Matin'
  return 'Toute la journée'
}

/** Vue Jour : plage horaire sur l'axe si l'absence est en heures ce jour-là, sinon null = toute l'amplitude. */
export function leaveDayGeometry(axis: DayAxis, l: LeaveSpan, refDay: string): { left: number; width: number } | null {
  if (l.hours_from === null || l.hours_to === null || l.starts_on !== refDay) return null
  return barGeometry(axis, l.hours_from / 60, l.hours_to / 60)
}

/** Abréviations MÉTIER des types d'absence du catalogue (vue Mois, marqueur « indisponible (CP) »). */
export const LEAVE_ABBR: Record<string, string> = {
  CP: 'CP', MALADIE: 'MAL', AT: 'AT', RECUP: 'RÉC', REPOS_COMP: 'RC', SANS_SOLDE: 'SS',
  EVENEMENT_FAMILIAL: 'ÉVT', AUTORISEE: 'ABS', AUTRE: 'AUT',
}

/**
 * Abréviation courte d'un type d'absence : table explicite par clé, sinon les
 * initiales du LIBELLÉ humain (2 à 3 lettres) — jamais un tronçon de la clé
 * technique (« SAN », « EVE », « AUT » ×2 étaient illisibles).
 */
export function leaveAbbr(typeKey: string, typeLabel?: string | null): string {
  const known = LEAVE_ABBR[typeKey.trim().toUpperCase()]
  if (known) return known
  const words = (typeLabel ?? '').split(/[\s'’\-/]+/).map((w) => w.normalize('NFD').replace(/[\u0300-\u036f]/g, '')).filter((w) => /^[A-Za-z]/.test(w) && !/^(de|du|des|la|le|les|en|et|ou|d|l|a|au|aux|pour|sans)$/i.test(w))
  if (words.length >= 2) return words.slice(0, 3).map((w) => w[0]!.toUpperCase()).join('')
  if (words.length === 1) return words[0]!.slice(0, 3).toUpperCase()
  return 'ABS'
}

/* ─── Composer d'absence : brouillon → corps HTTP (pur, testable) ─── */
export interface LeaveDraft {
  person_id: string; type_id: string
  from: string; to: string
  /** Journée seule : complète, demi-journée ou heures personnalisées. */
  duration: 'FULL' | 'HALF' | 'HOURS'
  half: 'AM' | 'PM'
  hoursFrom: string; hoursTo: string; hoursNextDay: boolean
  /** Plusieurs jours : demi-journées aux bornes. */
  startHalf: boolean; endHalf: boolean
  comment: string
  repeatWeekdays: number[]; repeatUntil: string | null
}

/**
 * Corps de `POST /api/v1/hr/leave/requests` (source PLANNING). Une journée :
 * Matin → end_half, Après-midi → start_half, heures → minutes locales
 * (> 1440 quand la fin est le lendemain). Plusieurs jours : demi-journées aux
 * bornes, jamais d'heures (règle serveur). `conflict_action` est ajouté par
 * l'appelant après un 409.
 */
export function leaveBody(d: LeaveDraft): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
  if (!d.person_id) return { ok: false, error: 'Choisissez un salarié.' }
  if (!d.type_id) return { ok: false, error: 'Choisissez un type d’absence.' }
  const from = d.from <= d.to ? d.from : d.to
  const to = d.from <= d.to ? d.to : d.from
  const body: Record<string, unknown> = { person_id: d.person_id, type_id: d.type_id, starts_on: from, ends_on: to, source: 'PLANNING' }
  if (from === to) {
    if (d.duration === 'HALF') { if (d.half === 'AM') body['end_half'] = true; else body['start_half'] = true }
    else if (d.duration === 'HOURS') {
      const a = hmToMinutes(d.hoursFrom); const b = hmToMinutes(d.hoursTo)
      if (a === null || b === null) return { ok: false, error: 'Heures de début et de fin requises.' }
      const end = b + (d.hoursNextDay ? 1440 : 0)
      if (end <= a) return { ok: false, error: 'La fin doit suivre le début (cochez « le lendemain » pour une nuit).' }
      body['hours_from'] = a; body['hours_to'] = end
    }
  } else {
    if (d.startHalf) body['start_half'] = true
    if (d.endHalf) body['end_half'] = true
  }
  const c = d.comment.trim(); if (c) body['comment'] = c
  if (d.repeatWeekdays.length > 0) {
    if (!d.repeatUntil) return { ok: false, error: 'Indiquez jusqu’à quelle date répéter.' }
    if (d.repeatUntil < to) return { ok: false, error: 'La date de fin de répétition précède l’absence.' }
    body['repeat'] = { weekdays: [...d.repeatWeekdays].sort((x, y) => x - y), until: d.repeatUntil }
  }
  return { ok: true, body }
}
